import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  useWindowDimensions,
  Platform,
} from 'react-native';
// FlowchartDesigner / FlowchartViewer no longer imported — drawing removed
// from Process on 2026-05-14 (SubProcess keeps it).
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb } from '../../components';
import { useResponsive, useRoleNavigation, useAutoTranslate, useUnsavedChangesWarning } from '../../hooks';
import { queryClient, queryKeys } from '../../providers/QueryProvider';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { processesApi, goalsApi, organizationalUnitsApi, risksApi } from '../../services/apiService';
import { StrategicGoalList, OrganizationalUnitList, Process, RiskType } from '../../types';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';

type NavigationProp = NativeStackNavigationProp<MainStackParamList>;
type RouteProps = RouteProp<MainStackParamList, 'AddProcess'>;

// ============================================================
// SVG Icons
// ============================================================
const ChevronLeftIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronDownIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronUpIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 15L12 9L6 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrashIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const MenuIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4 6H20M4 12H20M4 18H20" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);


// ============================================================
// Portal-based Dropdown Component with Fixed Positioning
// ============================================================
interface DropdownProps {
  isOpen: boolean;
  onClose: () => void;
  buttonRef: React.RefObject<View>;
  children: React.ReactNode;
  backgroundColor: string;
  borderColor: string;
}

const PortalDropdown = ({ isOpen, onClose, buttonRef, children, backgroundColor, borderColor }: DropdownProps) => {
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, openUpward: false, availableHeight: 300 });

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      (buttonRef.current as any).measureInWindow((x: number, y: number, width: number, height: number) => {
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - (y + height + 4);
        const spaceAbove = y - 4;
        const maxDropdown = 300;
        const openUpward = spaceBelow < 150 && spaceAbove > spaceBelow;
        setPosition({
          top: openUpward ? Math.max(4, y - Math.min(maxDropdown, spaceAbove)) : y + height + 4,
          left: x,
          width: width,
          openUpward,
          availableHeight: openUpward ? Math.min(maxDropdown, spaceAbove) : Math.min(maxDropdown, spaceBelow),
        });
      });
    }
  }, [isOpen, buttonRef]);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (e: Event) => {
      // Don't close if scrolling inside the dropdown itself
      if (dropdownRef.current && dropdownRef.current.contains(e.target as Node)) return;
      onClose();
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const dropdownMenu = (
    <>
      {/* eslint-disable-next-line */}
      <div
        onMouseDown={(e: any) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
        style={{
          position: 'fixed' as const,
          top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 9998,
        }}
      />
      <div
        ref={dropdownRef}
        onMouseDown={(e: any) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          width: position.width,
          backgroundColor: backgroundColor,
          border: `1px solid ${borderColor}`,
          borderRadius: 8,
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
          zIndex: 9999,
          maxHeight: position.availableHeight,
          overflowY: 'auto' as const,
        }}
      >
        {children}
      </div>
    </>
  );

  return ReactDOM.createPortal(dropdownMenu, document.body);
};

// Inline new-risk rows added from the process page. Persisted on save as risks
// of the SELECTED strategic goal (related_type='strategic_goal'). Existing goal
// risks are shown read-only for review — only NEW risks here get created.
interface RiskFormData {
  id: string;
  name: string;
  riskType: RiskType | '';
  probability: number; // 1-5
  impact: number;      // 1-5
  expectedImpact: string;
  mitigationActions: string;
  riskOwner: string;
  isExpanded: boolean;
}

export default function AddProcessScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const processId = route.params?.processId;
  const goalId = route.params?.goalId;
  const organizationalUnitId = route.params?.organizationalUnitId;
  const { navigateToHome, isViewOnly } = useRoleNavigation();
  const { user } = useAuth();
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const { translate, translateUnit } = useAutoTranslate();

  // Proposing/editing a process (عملية رئيسية) is governed by
  // `hrcpms.executive_items.create` — NOT the coarse `isViewOnly` flag. The
  // Processes list only shows the "إضافة عملية رئيسية" button to these users
  // (superuser | executive_items.create), so this screen must accept them too.
  // Without this, a user who holds the create perm but is also flagged
  // view-only (Director / Strategy-Team, or a superuser tripping isStrategyTeam)
  // gets silently bounced the instant they tap Add — i.e. "the button does
  // nothing".
  const canCreateProcess = !!(
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.executive_items.create')
  );

  // Theme colors for dark mode support
  const themeColors = {
    mainCard: colors.card,
    mainCardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : '#F1F5F9',
    sectionBorder: colors.border,
    sectionTitle: colors.textPrimary,
    label: colors.textPrimary,
    input: colors.card,
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    iconColor: colors.textMuted,
    dropdown: colors.card,
    dropdownBorder: colors.border,
    dropdownText: isDark ? '#6B7280' : '#9CA3AF',
    dropdownTextSelected: colors.textPrimary,
    dropdownMenu: colors.card,
    dropdownMenuBorder: colors.border,
    dropdownItem: colors.textPrimary,
    dropdownItemBorder: isDark ? colors.border : '#F3F4F6',
    riskCard: colors.card,
    riskCardBorder: colors.cardBorder,
    riskHeader: colors.card,
    addRiskButton: colors.card,
    addRiskButtonBorder: isDark ? '#4B5563' : '#CBD5E1',
    addRiskButtonText: colors.textPrimary,
    cancelButton: colors.card,
    cancelButtonBorder: colors.border,
    cancelButtonText: colors.textMuted,
    loadingText: colors.textMuted,
  };
  const isEditMode = !!processId;

  // Redirect view-only users (executive) - they cannot add/edit. Users who hold
  // the create permission are allowed through even when flagged view-only.
  useEffect(() => {
    if (isViewOnly && !canCreateProcess) {
      console.log('🚫 AddProcess: View-only user - redirecting');
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else {
        navigation.navigate('Processes' as any);
      }
    }
  }, [isViewOnly, canCreateProcess, navigation]);

  const scrollViewRef = useRef<ScrollView>(null);
  const goalDropdownButtonRef = useRef<View>(null);
  const unitDropdownButtonRef = useRef<View>(null);

  const { width } = useWindowDimensions();
  const { showSuccess, showError } = useNotification();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [goals, setGoals] = useState<StrategicGoalList[]>([]);
  const [units, setUnits] = useState<OrganizationalUnitList[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    code: '',
    strategicGoal: null as number | null,
    organizationalUnits: [] as number[],
    weight: '0',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Risks for the selected strategic goal — existing ones (read-only review)
  // plus new ones the user adds here (optional). New risks are saved on the
  // goal when the process is saved.
  const [goalRisks, setGoalRisks] = useState<any[]>([]);
  const [loadingGoalRisks, setLoadingGoalRisks] = useState(false);
  const [newRisks, setNewRisks] = useState<RiskFormData[]>([]);

  useUnsavedChangesWarning(!!formData.name || !!formData.description);

  // Dropdowns
  const [showGoalDropdown, setShowGoalDropdown] = useState(false);
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);

  const isSmallScreen = width < 768;

  useEffect(() => {
    fetchData();
  }, [processId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [goalsData, unitsData] = await Promise.all([
        goalsApi.list({ page_size: 100 }),
        organizationalUnitsApi.list({ page_size: 100 }),
      ]);
      setGoals(goalsData.results || []);
      setUnits(unitsData.results || []);

      // Pre-select organizational unit if passed from unit detail page
      if (!processId && organizationalUnitId) {
        setFormData(prev => ({ ...prev, organizationalUnits: [organizationalUnitId] }));
      }

      // If editing, fetch the process data
      if (processId) {
        const processData = await processesApi.get(processId);
        setFormData({
          name: processData.name || '',
          description: processData.description || '',
          code: processData.code || '',
          strategicGoal: processData.strategic_goal || null,
          organizationalUnits: processData.organizational_units || [],
          weight: processData.weight || '0',
        });
      } else if (goalId) {
        // Pre-select the goal when creating from GoalDetailScreen
        setFormData(prev => ({
          ...prev,
          strategicGoal: goalId,
        }));
      }
    } catch (err) {
      showError(t('processes.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('validation.processNameRequired');
    }
    // A عملية must be tied to a هدف استراتيجي — cannot be created/saved without one.
    if (!formData.strategicGoal) {
      newErrors.strategicGoal = t('validation.strategicGoalRequired');
    }
    if (formData.organizationalUnits.length === 0) {
      newErrors.organizationalUnits = t('validation.organizationalUnitRequired');
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return false;

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showError(t('validation.fillRequiredFields'));
      return;
    }

    setSubmitting(true);
    try {
      // Get user's organization
      const organizationId = user?.profile?.organization;
      if (!organizationId) {
        showError(t('common.organizationNotFound'));
        return;
      }

      // Prepare process data
      const processData: Partial<Process> = {
        department_id: organizationId,
        name: formData.name,
        description: formData.description || '',
        code: formData.code || '',
        strategic_goal: formData.strategicGoal || null,
        organizational_units: formData.organizationalUnits,
        weight: formData.weight || '0',
      } as any;

      if (isEditMode && processId) {
        // Update existing process
        console.log('Updating process with data:', processData);
        await processesApi.update(processId, processData);
      } else {
        // Create new process
        console.log('Creating process with data:', processData);
        await processesApi.create(processData);
      }

      // Create any NEW risks the user added — saved on the selected strategic
      // goal (related_type='strategic_goal'). Optional: only rows with a name
      // and a selected type are persisted. risk_value/level computed server-side.
      if (formData.strategicGoal) {
        const riskErrors: string[] = [];
        for (const risk of newRisks) {
          if (!risk.name.trim() || !risk.riskType) continue;
          try {
            await risksApi.create({
              name: risk.name.trim(),
              related_type: 'strategic_goal',
              related_id: formData.strategicGoal,
              risk_type: risk.riskType,
              probability: risk.probability,
              impact: risk.impact,
              expected_impact: risk.expectedImpact.trim(),
              mitigation_actions: risk.mitigationActions.trim(),
              risk_owner: risk.riskOwner.trim(),
            });
          } catch (riskErr: any) {
            console.warn('Risk save failed:', risk.name, riskErr?.response?.data);
            riskErrors.push(risk.name.trim());
          }
        }
        if (riskErrors.length > 0) {
          showError(`${t('risks.saveFailed')}: ${riskErrors.join('، ')}`);
        }
      }

      showSuccess(isEditMode ? t('processes.updateSuccess') : t('processes.createSuccess'));
      queryClient.invalidateQueries({ queryKey: queryKeys.processes.all });
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else {
        navigation.navigate('Processes' as any);
      }
    } catch (err: any) {
      console.error('Error saving process:', err);
      console.error('Error response:', err.response?.data);

      // Extract error message from various formats
      let errorMessage = isEditMode ? t('processes.updateFailed') : t('processes.createFailed');
      if (err.response?.data) {
        const data = err.response.data;
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data.detail) {
          errorMessage = data.detail;
        } else if (data.non_field_errors) {
          errorMessage = data.non_field_errors[0];
        } else {
          // Check for field-specific errors
          const fieldErrors = Object.entries(data)
            .filter(([key, value]) => Array.isArray(value))
            .map(([key, value]) => `${key}: ${(value as string[]).join(', ')}`)
            .join('\n');
          if (fieldErrors) {
            errorMessage = fieldErrors;
          }
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      showError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate('Processes' as any);
    }
  };

  // ---- Risks for the selected strategic goal ----
  // Load the goal's existing risks whenever the selected goal changes so the
  // user can review them before deciding to add a new one.
  useEffect(() => {
    let cancelled = false;
    const goalId = formData.strategicGoal;
    if (!goalId) {
      setGoalRisks([]);
      return;
    }
    setLoadingGoalRisks(true);
    risksApi.forItem('strategic_goal', goalId)
      .then((data) => { if (!cancelled) setGoalRisks(Array.isArray(data) ? data : []); })
      .catch((err) => { if (!cancelled) { console.warn('Failed to load goal risks:', err); setGoalRisks([]); } })
      .finally(() => { if (!cancelled) setLoadingGoalRisks(false); });
    return () => { cancelled = true; };
  }, [formData.strategicGoal]);

  const addNewRisk = () => {
    setNewRisks(prev => [...prev, {
      id: `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: '',
      riskType: '',
      probability: 3,
      impact: 3,
      expectedImpact: '',
      mitigationActions: '',
      riskOwner: '',
      isExpanded: true,
    }]);
  };

  const updateNewRisk = (id: string, field: keyof RiskFormData, value: any) => {
    setNewRisks(prev => prev.map(r => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const removeNewRisk = (id: string) => {
    setNewRisks(prev => prev.filter(r => r.id !== id));
  };

  const toggleNewRiskExpanded = (id: string) => {
    setNewRisks(prev => prev.map(r => (r.id === id ? { ...r, isExpanded: !r.isExpanded } : r)));
  };

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.primary} />
          <Text style={[styles.loadingText, { color: themeColors.loadingText }]}>{t('common.loadingData')}</Text>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView ref={scrollViewRef} style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={[styles.container, isSmallScreen && styles.containerSmall]}>
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: t('navigation.home') },
              { label: t('navigation.processes'), route: 'Processes' },
              { label: isEditMode ? t('processes.editProcess') : t('processes.addMainProcess') },
            ]}
          />
   {/* Main Card Container */}
        <View style={[styles.mainCard, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder, overflow: 'visible', zIndex: 10 }]}>
          {/* Title */}
          <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>{isEditMode ? t('processes.editProcess') : t('processes.addMainProcess')}</Text>

          {/* Basic Information Section */}
          <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder, zIndex: (showGoalDropdown || showUnitDropdown) ? 9999 : 20, overflow: 'visible' }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('common.basicInfo')}</Text>

            <View style={[styles.formRow, isSmallScreen && styles.formRowSmall, { zIndex: showGoalDropdown ? 9999 : 10, overflow: 'visible' }]}>
              {/* Process Name - RIGHT side in RTL */}
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={[styles.label, { color: themeColors.label }]}>
                  {t('processes.processName')} <Text style={styles.required}>*</Text>
                </Text>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }, errors.name ? styles.inputError : undefined]}
                  value={formData.name}
                  onChangeText={(text) => setFormData({ ...formData, name: text })}
                  placeholder=""
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                />
                {errors.name && <Text style={styles.errorText}>{errors.name}</Text>}
              </View>

              {/* Strategic Goal - LEFT side in RTL */}
              <View style={[styles.formGroup, styles.formGroupHalf, { overflow: 'visible' }]}>
                <Text style={[styles.label, { color: themeColors.label }]}>
                  {t('goals.strategicGoal')} <Text style={styles.required}>*</Text>
                </Text>
                <View style={styles.dropdownWrapper}>
                  <TouchableOpacity
                    ref={goalDropdownButtonRef as any}
                    style={[styles.dropdown, { backgroundColor: themeColors.dropdown, borderColor: themeColors.dropdownBorder }, errors.strategicGoal ? styles.inputError : undefined]}
                    onPress={() => {
                      setShowGoalDropdown(!showGoalDropdown);
                      setShowUnitDropdown(false);
                    }}
                  >
                    <ChevronDownIcon size={16} color={themeColors.iconColor} />
                    <Text style={[styles.dropdownText, { color: themeColors.dropdownText }, formData.strategicGoal ? { color: themeColors.dropdownTextSelected } : undefined]}>
                      {formData.strategicGoal
                        ? translate(goals.find(g => g.id === formData.strategicGoal)?.title) || t('goals.selectStrategicGoal')
                        : t('goals.selectStrategicGoal')}
                    </Text>
                  </TouchableOpacity>
                  <PortalDropdown
                    isOpen={showGoalDropdown}
                    onClose={() => setShowGoalDropdown(false)}
                    buttonRef={goalDropdownButtonRef}
                    backgroundColor={themeColors.dropdownMenu}
                    borderColor={themeColors.dropdownMenuBorder}
                  >
                    <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                      {/* No "بدون هدف" option — a عملية must be linked to a هدف استراتيجي. */}
                      {goals.map(goal => (
                        <TouchableOpacity
                          key={goal.id}
                          style={[styles.dropdownItem, { borderBottomColor: themeColors.dropdownItemBorder }]}
                          onPress={() => {
                            setFormData({ ...formData, strategicGoal: goal.id });
                            setShowGoalDropdown(false);
                          }}
                        >
                          <Text style={[styles.dropdownItemText, { color: themeColors.dropdownItem }]}>{translate(goal.title)}</Text>
                        </TouchableOpacity>
                      ))}
                    </ScrollView>
                  </PortalDropdown>
                </View>
                {errors.strategicGoal && <Text style={styles.errorText}>{errors.strategicGoal}</Text>}
              </View>
            </View>

            {/* Process Code (رمز العملية) — segment of the operational code path */}
            <View style={styles.formGroup}>
              <Text style={[styles.label, { color: themeColors.label }]}>{t('operations.operationCode')}</Text>
              <TextInput
                style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={formData.code}
                onChangeText={(text) => setFormData({ ...formData, code: text })}
                placeholder={t('operations.operationCodeHint')}
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
              />
            </View>

            {/* Description */}
            <View style={styles.formGroup}>
              <Text style={[styles.label, { color: themeColors.label }]}>
                {t('processes.processDescription')} <Text style={styles.required}>*</Text>
              </Text>
              <TextInput
                style={[styles.input, styles.textarea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={formData.description}
                onChangeText={(text) => setFormData({ ...formData, description: text })}
                placeholder=""
                placeholderTextColor={themeColors.placeholder}
                multiline
                numberOfLines={3}
                textAlign="right"
                textAlignVertical="top"
              />
            </View>

            {/* Organizational Units - Multi-select */}
            <View style={styles.formGroup}>
              <Text style={[styles.label, { color: themeColors.label }]}>
                {t('unitGoals.organizationalUnit')} <Text style={styles.required}>*</Text>
              </Text>
              <View style={styles.dropdownWrapper}>
                <TouchableOpacity
                  ref={unitDropdownButtonRef as any}
                  style={[styles.dropdown, { backgroundColor: themeColors.dropdown, borderColor: themeColors.dropdownBorder, minHeight: 44, height: 'auto' as any, flexWrap: 'wrap' as any }, errors.organizationalUnits ? styles.inputError : undefined]}
                  onPress={() => {
                    setShowUnitDropdown(!showUnitDropdown);
                    setShowGoalDropdown(false);
                  }}
                >
                  <ChevronDownIcon size={16} color={themeColors.iconColor} />
                  {formData.organizationalUnits.length > 0 ? (
                    <View style={{ flex: 1, flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 6 }}>
                      {formData.organizationalUnits.map(uid => {
                        const unit = units.find(u => u.id === uid);
                        return (
                          <View key={uid} style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#FEF2F2', borderRadius: 12, paddingHorizontal: 8, paddingVertical: 4 }}>
                            <TouchableOpacity
                              onPress={(e) => {
                                e.stopPropagation();
                                setFormData({ ...formData, organizationalUnits: formData.organizationalUnits.filter(id => id !== uid) });
                              }}
                              style={{ marginLeft: 4 }}
                            >
                              <Text style={{ color: '#A9211F', fontSize: 14, fontWeight: '700' }}>×</Text>
                            </TouchableOpacity>
                            <Text style={{ color: '#A9211F', fontSize: 12, fontFamily: 'JF-Flat-Regular' }} numberOfLines={1}>
                              {translateUnit(unit?.name_ar || unit?.name) || `#${uid}`}
                            </Text>
                          </View>
                        );
                      })}
                    </View>
                  ) : (
                    <Text style={[styles.dropdownText, { color: themeColors.dropdownText }]}>
                      {t('common.selectResponsible')}
                    </Text>
                  )}
                </TouchableOpacity>
                <PortalDropdown
                  isOpen={showUnitDropdown}
                  onClose={() => setShowUnitDropdown(false)}
                  buttonRef={unitDropdownButtonRef}
                  backgroundColor={themeColors.dropdownMenu}
                  borderColor={themeColors.dropdownMenuBorder}
                >
                  <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                    {units.length === 0 ? (
                      <View style={[styles.dropdownItem, { borderBottomColor: themeColors.dropdownItemBorder }]}>
                        <Text style={[styles.dropdownItemText, { color: themeColors.dropdownItem }]}>{t('unitGoals.noUnits')}</Text>
                      </View>
                    ) : (
                      units.map(unit => {
                        const isSelected = formData.organizationalUnits.includes(unit.id);
                        return (
                          <TouchableOpacity
                            key={unit.id}
                            style={[styles.dropdownItem, { borderBottomColor: themeColors.dropdownItemBorder }, isSelected && { backgroundColor: '#FEF2F2' }]}
                            onPress={() => {
                              if (isSelected) {
                                setFormData({ ...formData, organizationalUnits: formData.organizationalUnits.filter(id => id !== unit.id) });
                              } else {
                                setFormData({ ...formData, organizationalUnits: [...formData.organizationalUnits, unit.id] });
                              }
                              setErrors({ ...errors, organizationalUnits: '' });
                            }}
                          >
                            <View style={{ width: 20, height: 20, borderRadius: 4, borderWidth: 1.5, borderColor: isSelected ? '#A9211F' : '#D1D5DB', backgroundColor: isSelected ? '#A9211F' : 'transparent', alignItems: 'center', justifyContent: 'center', marginLeft: 8 }}>
                              {isSelected && <Text style={{ color: '#FFF', fontSize: 12, fontWeight: '700' }}>✓</Text>}
                            </View>
                            <Text style={[styles.dropdownItemText, { color: themeColors.dropdownItem }, isSelected && { fontWeight: '600' }]}>{translateUnit(unit.name_ar || unit.name)}</Text>
                          </TouchableOpacity>
                        );
                      })
                    )}
                  </ScrollView>
                </PortalDropdown>
              </View>
              {errors.organizationalUnits && <Text style={styles.errorText}>{errors.organizationalUnits}</Text>}
            </View>
          </View>

          {/* Flowchart section removed 2026-05-14 — drawings live on
              SubProcess only. Process is just a container; its
              workflow is described by the sub-processes underneath. */}

          {/* Risks Section — only when a strategic goal is selected. Lets the
              user review the goal's existing risks and optionally add new ones
              (saved on the goal). Entirely optional. */}
          {formData.strategicGoal && (
            <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder }]}>
              <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>
                {t('risks.relatedRisks')} <Text style={[styles.label, { color: themeColors.placeholder }]}>({t('common.optional')})</Text>
              </Text>

              {/* Existing goal risks — read-only review */}
              {loadingGoalRisks ? (
                <ActivityIndicator size="small" color={COLORS.primary} style={{ marginVertical: 12 }} />
              ) : goalRisks.length > 0 ? (
                <View style={{ marginBottom: 12, gap: 8 }}>
                  {goalRisks.map((gr) => (
                    <View key={gr.id} style={[styles.existingRiskRow, { borderColor: themeColors.riskCardBorder, backgroundColor: themeColors.riskCard }]}>
                      <View style={[styles.riskLevelDot, { backgroundColor:
                        gr.risk_level === 'critical' ? '#DC2626'
                        : gr.risk_level === 'high' ? '#EF4444'
                        : gr.risk_level === 'medium' ? '#EAB308'
                        : '#22C55E' }]} />
                      <View style={{ flex: 1 }}>
                        <Text style={[styles.existingRiskName, { color: themeColors.inputText }]} numberOfLines={1}>{gr.name}</Text>
                        <Text style={[styles.existingRiskMeta, { color: themeColors.placeholder }]}>
                          {gr.risk_type_display || ''}{gr.risk_level_display ? ` · ${gr.risk_level_display}` : ''}{gr.risk_value != null ? ` · ${gr.risk_value}` : ''}
                        </Text>
                      </View>
                    </View>
                  ))}
                </View>
              ) : (
                <Text style={[styles.existingRiskMeta, { color: themeColors.placeholder, marginBottom: 12, textAlign: 'right' }]}>
                  {t('risks.noGoalRisks')}
                </Text>
              )}

              {/* New risk cards */}
              {newRisks.map((risk, index) => {
                const riskValue = risk.probability * risk.impact;
                return (
                  <View key={risk.id} style={[styles.riskCard, { backgroundColor: themeColors.riskCard, borderColor: themeColors.riskCardBorder }]}>
                    <View style={[styles.riskHeader, { backgroundColor: themeColors.riskHeader }]}>
                      <View style={{ flexDirection: 'row', gap: 8 }}>
                        <TouchableOpacity onPress={() => toggleNewRiskExpanded(risk.id)}>
                          {risk.isExpanded ? (
                            <ChevronUpIcon size={20} color={themeColors.iconColor} />
                          ) : (
                            <ChevronDownIcon size={20} color={themeColors.iconColor} />
                          )}
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => removeNewRisk(risk.id)}>
                          <TrashIcon size={18} color={themeColors.iconColor} />
                        </TouchableOpacity>
                      </View>
                      <Text style={[styles.existingRiskName, { color: themeColors.inputText, flex: 1, textAlign: 'right' }]} numberOfLines={1}>
                        {risk.name || `${t('risks.riskName')} ${index + 1}`}
                      </Text>
                    </View>

                    {risk.isExpanded && (
                      <View style={{ padding: 16, gap: 12 }}>
                        {/* Name */}
                        <View style={styles.formGroup}>
                          <Text style={[styles.label, { color: themeColors.label }]}>
                            {t('risks.riskName')} <Text style={styles.required}>*</Text>
                          </Text>
                          <TextInput
                            style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                            value={risk.name}
                            onChangeText={(v) => updateNewRisk(risk.id, 'name', v)}
                            placeholder={t('risks.enterRiskName')}
                            placeholderTextColor={themeColors.placeholder}
                            textAlign="right"
                          />
                        </View>

                        {/* Risk type chips */}
                        <View style={styles.formGroup}>
                          <Text style={[styles.label, { color: themeColors.label }]}>
                            {t('risks.riskType')} <Text style={styles.required}>*</Text>
                          </Text>
                          <View style={styles.riskChipRow}>
                            {[
                              { value: 'strategic', label: t('risks.typeStrategic') },
                              { value: 'operational', label: t('risks.typeOperational') },
                              { value: 'financial', label: t('risks.typeFinancial') },
                              { value: 'compliance', label: t('risks.typeCompliance') },
                            ].map((opt) => (
                              <TouchableOpacity
                                key={opt.value}
                                style={[styles.riskChip, { borderColor: themeColors.inputBorder }, risk.riskType === opt.value && styles.riskChipActive]}
                                onPress={() => updateNewRisk(risk.id, 'riskType', opt.value)}
                              >
                                <Text style={[styles.riskChipText, { color: themeColors.inputText }, risk.riskType === opt.value && styles.riskChipTextActive]}>{opt.label}</Text>
                              </TouchableOpacity>
                            ))}
                          </View>
                        </View>

                        {/* Owner */}
                        <View style={styles.formGroup}>
                          <Text style={[styles.label, { color: themeColors.label }]}>{t('risks.riskOwner')}</Text>
                          <TextInput
                            style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                            value={risk.riskOwner}
                            onChangeText={(v) => updateNewRisk(risk.id, 'riskOwner', v)}
                            placeholder={t('risks.riskOwnerPlaceholder')}
                            placeholderTextColor={themeColors.placeholder}
                            textAlign="right"
                          />
                        </View>

                        {/* Probability + Impact (1-5) */}
                        <View style={[styles.formRow, isSmallScreen && { flexDirection: 'column' }]}>
                          <View style={[styles.formGroup, { flex: 1 }]}>
                            <Text style={[styles.label, { color: themeColors.label }]}>{t('risks.probability')} (1-5)</Text>
                            <View style={styles.riskChipRow}>
                              {[1, 2, 3, 4, 5].map((n) => (
                                <TouchableOpacity
                                  key={n}
                                  style={[styles.riskScaleChip, { borderColor: themeColors.inputBorder }, risk.probability === n && styles.riskChipActive]}
                                  onPress={() => updateNewRisk(risk.id, 'probability', n)}
                                >
                                  <Text style={[styles.riskChipText, { color: themeColors.inputText }, risk.probability === n && styles.riskChipTextActive]}>{n}</Text>
                                </TouchableOpacity>
                              ))}
                            </View>
                          </View>
                          <View style={[styles.formGroup, { flex: 1 }]}>
                            <Text style={[styles.label, { color: themeColors.label }]}>{t('risks.impact')} (1-5)</Text>
                            <View style={styles.riskChipRow}>
                              {[1, 2, 3, 4, 5].map((n) => (
                                <TouchableOpacity
                                  key={n}
                                  style={[styles.riskScaleChip, { borderColor: themeColors.inputBorder }, risk.impact === n && styles.riskChipActive]}
                                  onPress={() => updateNewRisk(risk.id, 'impact', n)}
                                >
                                  <Text style={[styles.riskChipText, { color: themeColors.inputText }, risk.impact === n && styles.riskChipTextActive]}>{n}</Text>
                                </TouchableOpacity>
                              ))}
                            </View>
                          </View>
                        </View>

                        <Text style={[styles.existingRiskMeta, { color: themeColors.inputText, textAlign: 'right' }]}>
                          قيمة الخطر: {riskValue} ({risk.probability} × {risk.impact})
                        </Text>

                        {/* Expected impact */}
                        <View style={styles.formGroup}>
                          <Text style={[styles.label, { color: themeColors.label }]}>{t('risks.expectedImpact')}</Text>
                          <TextInput
                            style={[styles.input, styles.textarea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                            value={risk.expectedImpact}
                            onChangeText={(v) => updateNewRisk(risk.id, 'expectedImpact', v)}
                            placeholder={t('risks.expectedImpactPlaceholder')}
                            placeholderTextColor={themeColors.placeholder}
                            multiline
                            textAlign="right"
                            textAlignVertical="top"
                          />
                        </View>

                        {/* Mitigation */}
                        <View style={styles.formGroup}>
                          <Text style={[styles.label, { color: themeColors.label }]}>{t('risks.mitigationActions')}</Text>
                          <TextInput
                            style={[styles.input, styles.textarea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                            value={risk.mitigationActions}
                            onChangeText={(v) => updateNewRisk(risk.id, 'mitigationActions', v)}
                            placeholder={t('risks.mitigationActions')}
                            placeholderTextColor={themeColors.placeholder}
                            multiline
                            textAlign="right"
                            textAlignVertical="top"
                          />
                        </View>
                      </View>
                    )}
                  </View>
                );
              })}

              {/* Add Risk Button */}
              <TouchableOpacity style={[styles.addRiskButton, { backgroundColor: themeColors.addRiskButton, borderColor: themeColors.addRiskButtonBorder }]} onPress={addNewRisk}>
                <PlusIcon size={16} color={themeColors.addRiskButtonText} />
                <Text style={[styles.addRiskButtonText, { color: themeColors.addRiskButtonText }]}>{t('risks.addRiskToGoal')}</Text>
              </TouchableOpacity>
            </View>
          )}

          {/* Footer Buttons */}
          <View style={styles.footer}>
            <TouchableOpacity style={[styles.cancelButton, { backgroundColor: themeColors.cancelButton, borderColor: themeColors.cancelButtonBorder }]} onPress={handleCancel}>
              <Text style={[styles.cancelButtonText, { color: themeColors.cancelButtonText }]}>{t('common.cancel')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.saveButton, submitting && styles.saveButtonDisabled]}
              onPress={handleSave}
              disabled={submitting}
            >
              {submitting ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>{isEditMode ? t('common.update') : t('common.save')}</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
        </View>
      </ScrollView>
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  container: {
    flex: 1,
    padding: 24,
    paddingTop: 16,
    overflow: 'visible',
  },
  containerSmall: {
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: COLORS.neutral600,
  },

  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    marginBottom: 16,
    gap: 8,
  },
  breadcrumbItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  breadcrumbText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  breadcrumbActive: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: COLORS.primary,
  },

    mainCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'visible',
  },

  // Title
  pageTitle: {
    fontSize: 24,
    fontFamily: FONTS.medium,
    color: COLORS.neutral900,
    textAlign: 'right',
    marginBottom: 24,
  },

  // Section
  section: {
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    padding: 24,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    overflow: 'visible',
  },
  sectionTitle: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: COLORS.neutral900,
    textAlign: 'right',
    marginBottom: 20,
  },

  // Form
  formRow: {
    flexDirection: 'row-reverse',
    gap: 16,
    marginBottom: 16,
    overflow: 'visible',
  },
  formRowSmall: {
    flexDirection: 'column',
  },
  formGroup: {
    marginBottom: 16,
    overflow: 'visible',
  },
  formGroupHalf: {
    flex: 1,
    overflow: 'visible',
  },
  label: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: COLORS.neutral700,
    textAlign: 'right',
    marginBottom: 8,
  },
  required: {
    color: COLORS.primary,
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 7.5,
    minHeight: 36,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    writingDirection: 'rtl',
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  inputError: {
    borderColor: '#EF4444',
  },
  textarea: {
    minHeight: 60,
    textAlignVertical: 'top',
  },
  errorText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#EF4444',
    textAlign: 'right',
    marginTop: 4,
  },

  // Dropdown
  dropdownWrapper: {
    position: 'relative',
    zIndex: 10,
    overflow: 'visible',
  },
  dropdown: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 7.5,
    minHeight: 36,
    gap: 8,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  dropdownText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#9CA3AF',
    flex: 1,
    textAlign: 'right',
    writingDirection: 'rtl',
  },
  dropdownTextSelected: {
    color: COLORS.neutral900,
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    left: 0,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    marginTop: 4,
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
    elevation: 10,
    zIndex: 9999,
    maxHeight: 200,
  },
  dropdownItem: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  dropdownItemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: COLORS.neutral700,
    textAlign: 'right',
    writingDirection: 'rtl',
  },

  // Weight Input
  weightInputRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 12,
  },
  // Risk card
  riskCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    marginBottom: 16,
    overflow: 'visible',
  },
  riskHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    backgroundColor: '#FFFFFF',
    gap: 12,
  },

  // Add Risk Button
  addRiskButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 7.5,
    minHeight: 36,
    gap: 8,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  addRiskButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
  },

  // Risk section
  existingRiskRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  riskLevelDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#22C55E',
  },
  existingRiskName: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
  },
  existingRiskMeta: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    textAlign: 'right',
  },
  riskChipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  riskChip: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 14,
    minHeight: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  riskScaleChip: {
    flex: 1,
    minWidth: 40,
    minHeight: 40,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  riskChipActive: {
    backgroundColor: COLORS.primary,
    borderColor: COLORS.primary,
  },
  riskChipText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
  },
  riskChipTextActive: {
    color: '#FFFFFF',
  },

  // Footer
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 12,
    marginTop: 8,
    marginBottom: 24,
  },
  saveButton: {
    backgroundColor: COLORS.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    minWidth: 80,
    alignItems: 'center',
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  cancelButton: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  cancelButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
});
